import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { EventInfoWithConsentResponse, InvestorMeetingCreateRequest, InvestorMeetingRequestControllerService, InvestorMeetingRequestResponse, InvestorMeetingUpdateRequest, InvestorSlotControllerService, InvestorSlotUpdate, InvestorSlotUpdateRequest, RegisterInvestorResponse, RegisterInvestorUpdateRequest } from '@ent-regis/entregis-ts-angular';
import { combineLatest } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { msg } from '../constants/msg';
import { shouldShowInstitutionalInvestorType } from '../functions/register-investor.function';
import { RegisterInvestorService } from '../core/service/register-investor.service';
import { AlertService } from '../shared/services/alert.service';
import { EventService } from '../shared/services/event.service';
import { FormUtilsService } from '../shared/services/form-utils.service';
import { LoginLayoutComponent } from '../layouts/login-layout/login-layout/login-layout.component';

type InvestorTab = 'information' | 'company' | 'availableTime' | 'meetingSchedule' | 'hotel';

@Component({
  selector: 'app-investor',
  templateUrl: './investor.component.html',
  styleUrls: ['./investor.component.scss']
})
export class InvestorComponent implements OnInit {

  public now = new Date();
  eventTypeEnum = EventInfoWithConsentResponse.EventTypeEnum;
  eventType: EventInfoWithConsentResponse.EventTypeEnum;
  registerInvestorId: number;
  investorProfile: RegisterInvestorResponse;
  eventInfoWithConsent: EventInfoWithConsentResponse;
  isShowHotel: boolean;
  startDate: Date;
  endDate: Date;
  isClosedRegis: boolean = false;
  leave: boolean = true;

  form!: FormGroup;
  defaultMeetingType: InvestorMeetingCreateRequest.MeetingTypeEnum = InvestorMeetingCreateRequest.MeetingTypeEnum.GROUP;
  defaultMeetingPriority = InvestorMeetingCreateRequest.MeetingPriorityEnum.MEDIUM;
  attendancePreference: RegisterInvestorResponse.AttendancePreferenceEnum | undefined;

  activeTab: InvestorTab = 'information';

  private lastSavedProfile: any = null;
  private lastSavedCompanySelection: any = null;
  private lastSavedTimeSelection: any = null;

  constructor(
    private route: ActivatedRoute,
    private registerInvestorService: RegisterInvestorService,
    private eventService: EventService,
    private alert: AlertService,
    private formUtils: FormUtilsService,
    private investorSlotService: InvestorSlotControllerService,
    private investorMeetingRequestService: InvestorMeetingRequestControllerService,
    private loginLayout: LoginLayoutComponent,
  ) { }

  get profileForm(): FormGroup {
    return this.form.get('profile') as FormGroup;
  }

  get companySelectionForm(): FormGroup {
    return this.form.get('companySelection') as FormGroup;
  }

  get timeSelectionForm(): FormGroup {
    return this.form.get('timeSelection') as FormGroup;
  }

  ngOnInit() {
    this.getRegisterInvestorId();
    this.initializePage();
  }

  private initializePage(): void {
    combineLatest([
      this.eventService.eventSubject.pipe(first(event => event != null)),
      this.registerInvestorService.investorProfile.pipe(first()),
      this.registerInvestorService.investorMeetingRequest.pipe(first()),
      this.registerInvestorService.investorSlots.pipe(first()),
    ])
    .subscribe(([event, investor, meetingRequest, slots]) => {
        this.setEventInfo(event);
        this.attendancePreference = investor.attendancePreference;
        this.defaultMeetingType = this.getDefaultMeetingType(this.attendancePreference);

        this.form = new FormGroup({
          profile: this.createProfileForm(),
          companySelection: new FormGroup({ companies: new FormArray([]) }),
          timeSelection: new FormGroup({ slots: new FormArray([]) })
        });
        this.setProfileForm(investor);
        this.setCompanyForm(meetingRequest);
        this.setTimeSelectionForm(slots);

        this.captureProfileSnapshot();
        this.captureCompanySelectionSnapshot();
        this.captureTimeSelectionSnapshot();

        this.investorProfile = investor;
        this.registerInvestorService.isCloseRegister.next(investor.isCloseRegister);
        this.loginLayout.setBackgroundUrl(this.eventInfoWithConsent.bgUrl);
    });
  }

  private setEventInfo(event: EventInfoWithConsentResponse): void {
    this.isShowHotel = event.isShowHotel;
    this.startDate = event.startDateRegisterInvestor;
    this.endDate = event.endDateRegisterInvestor;
    this.isClosedRegis = event.isCloseRegisterInvestor;
    this.eventType = event.eventType;
    this.eventInfoWithConsent = event;
  }

  private createProfileForm(): FormGroup {
    return new FormGroup({
      coHostName: new FormControl(null, this.eventInfoWithConsent.isShowSalePersonForInvestorRegistration ? Validators.required : null),
      brokerName: new FormControl(''),
      hasSalePerson: new FormControl(true),
      seminarSubEvent: new FormControl(null),
      attendancePreference: new FormControl('', this.eventType === EventInfoWithConsentResponse.EventTypeEnum.THAILANDFOCUS ? Validators.required : null),
      companyName: new FormControl('', Validators.required),
      firstName: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      mobileNumber: new FormControl('', Validators.required),
      extNumber: new FormControl(''),
      jobTitle: new FormControl('', Validators.required),
      department: new FormControl('', Validators.required),
      countryId: new FormControl(null, Validators.required),
      institutionalInvestorTypeId: new FormControl(null, shouldShowInstitutionalInvestorType(this.eventInfoWithConsent) ? Validators.required : null),
      otherInstitutionalInvestorType: new FormControl(''),
      assistantFirstName: new FormControl(''),
      assistantLastName: new FormControl(''),
      assistantEmail: new FormControl('', Validators.email),
      assistantMobileNumber: new FormControl(''),
      assistantExtNumber: new FormControl('')
    }, {
      validators: this.duplicateEmailValidator()
    });
  }

  duplicateEmailValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
    const attendeeControl = group.get('email');
    const assistantContol = group.get('assistantEmail');
    const attendeeEmail = attendeeControl.value;
    const assistantEmail = assistantContol.value;

    this.formUtils.removeError(attendeeControl, 'duplicateEmail');
    this.formUtils.removeError(assistantContol, 'duplicateEmail');

    if (!attendeeEmail || !assistantEmail) {
      return null;
    }
    
    if (attendeeEmail === assistantEmail) {
      this.formUtils.setError(attendeeControl, 'duplicateEmail', true);
      this.formUtils.setError(assistantContol, 'duplicateEmail', true);
      return { duplicateEmail: true };
    }

    return null;
  };}

  async goTab(tab: InvestorTab): Promise<void> {
    if (this.activeTab === tab) {
      return;
    }
    if (this.activeTab === 'information' && this.hasProfileChanged()) {
      this.discardProfileModal(tab);
      return;
    }
    if (this.activeTab === 'company' && this.hasCompanySelectionChanged()) {
      this.discardCompanySelectionModal(tab);
      return;
    }
    if (this.activeTab === 'availableTime' && this.hasTimeSelectionChanged()) {
      this.discardTimeSelectionModal(tab);
      return;
    }

    this.activeTab = tab;
  }

  private captureProfileSnapshot(): void {
    this.lastSavedProfile = JSON.parse(JSON.stringify(this.profileForm.value));
  }

  private hasProfileChanged(): boolean {
    const currentValue = {
      ...this.profileForm.value,
      brokerName: this.profileForm.value.brokerName ? this.profileForm.value.brokerName : this.profileForm.value.coHostName,  
      mobileNumber:
        this.profileForm.value.mobileNumber && this.profileForm.value.mobileNumber.number
          ? this.profileForm.value.mobileNumber.number : this.profileForm.value.mobileNumber,
      assistantMobileNumber:
        this.profileForm.value.assistantMobileNumber && this.profileForm.value.assistantMobileNumber.number
          ? this.profileForm.value.assistantMobileNumber.number : this.profileForm.value.assistantMobileNumber
    };
    const savedValue = {
      ...this.lastSavedProfile,
      brokerName: this.profileForm.value.brokerName ? this.profileForm.value.brokerName : this.profileForm.value.coHostName,  
      mobileNumber:
        this.lastSavedProfile.mobileNumber && this.lastSavedProfile.mobileNumber.number
          ? this.lastSavedProfile.mobileNumber.number : this.lastSavedProfile.mobileNumber,
      assistantMobileNumber:
        this.lastSavedProfile.assistantMobileNumber && this.lastSavedProfile.assistantMobileNumber.number
          ? this.lastSavedProfile.assistantMobileNumber.number : this.lastSavedProfile.assistantMobileNumber
    };

    return JSON.stringify(currentValue) !== JSON.stringify(savedValue);
  }

  private restoreProfileSnapshot(): void {
    this.profileForm.reset(this.lastSavedProfile);
    this.profileForm.markAsPristine();
    this.profileForm.markAsUntouched();
  }

  discardProfileModal(tab: InvestorTab): void {
    this.alert.showDiscardChanges().then((result) => {
      if (result.isConfirmed) {
        this.restoreProfileSnapshot();
        this.activeTab = tab;
      }
    });
  }

  private captureCompanySelectionSnapshot(): void {
    this.lastSavedCompanySelection = JSON.parse(JSON.stringify(this.companySelectionForm.value));
  }

  private hasCompanySelectionChanged(): boolean {
    if (!this.lastSavedCompanySelection) {
      return false;
    }

    return JSON.stringify(this.companySelectionForm.value) !== JSON.stringify(this.lastSavedCompanySelection);
  }

  private restoreCompanySelectionSnapshot(): void {
    const snapshotCompanies = this.lastSavedCompanySelection && this.lastSavedCompanySelection.companies
      ? this.lastSavedCompanySelection.companies
      : [];

    const companyControls = snapshotCompanies.map((company: any) => new FormGroup({
      registerCompanyId: new FormControl(company.registerCompanyId),
      investorMeetingRequestId: new FormControl(company.investorMeetingRequestId),
      meetingType: new FormControl(company.meetingType),
      meetingPriority: new FormControl(company.meetingPriority),
    }));

    this.companySelectionForm.setControl('companies', new FormArray(companyControls));
    this.companySelectionForm.markAsPristine();
    this.companySelectionForm.markAsUntouched();
  }

  discardCompanySelectionModal(tab: InvestorTab): void {
    this.alert.showDiscardChanges().then((result) => {
      if (result.isConfirmed) {
        this.restoreCompanySelectionSnapshot();
        this.activeTab = tab;
      }
    });
  }

  private captureTimeSelectionSnapshot(): void {
    this.lastSavedTimeSelection = JSON.parse(JSON.stringify(this.timeSelectionForm.value));
  }

  private hasTimeSelectionChanged(): boolean {
    if (!this.lastSavedTimeSelection) {
      return false;
    }

    return JSON.stringify(this.timeSelectionForm.value) !== JSON.stringify(this.lastSavedTimeSelection);
  }

  private restoreTimeSelectionSnapshot(): void {
    const snapshotSlots = this.lastSavedTimeSelection && this.lastSavedTimeSelection.slots
      ? this.lastSavedTimeSelection.slots
      : [];

    const slotControls = snapshotSlots.map((slot: any) => new FormGroup({
      slotDate: new FormControl(slot.slotDate),
      slotId: new FormControl(slot.slotId),
      slotPeriods: this.createSlotPeriodsFormArray(slot.slotPeriods)
    }));

    this.timeSelectionForm.setControl('slots', new FormArray(slotControls, this.validateAvailableTime()));
    this.timeSelectionForm.markAsPristine();
    this.timeSelectionForm.markAsUntouched();
  }

  discardTimeSelectionModal(tab: InvestorTab): void {
    this.alert.showDiscardChanges().then((result) => {
      if (result.isConfirmed) {
        this.restoreTimeSelectionSnapshot();
        this.activeTab = tab;
      }
    });
  }

  getRegisterInvestorId() {
    this.route.paramMap
      .pipe(map((x: ParamMap) => x.get('registerInvestorId')))
      .subscribe((x: string) => {
        this.registerInvestorService.registerInvestorId.next(+x);
      });
  }

  private setCompanyForm(meetingList: InvestorMeetingRequestResponse[]): void {
    const meetings = Array.isArray(meetingList) ? meetingList : [];

    const companyControls = meetings
      .filter(meetingReq => meetingReq.investorMeetingRequestId)
      .map((meetingReq) => {
        const normalized = this.normalizeSelection(
          meetingReq.meetingType as InvestorMeetingCreateRequest.MeetingTypeEnum,
          meetingReq.priority as InvestorMeetingCreateRequest.MeetingPriorityEnum
        );

        return new FormGroup({
          registerCompanyId: new FormControl(meetingReq.registerCompanyId),
          investorMeetingRequestId: new FormControl(meetingReq.investorMeetingRequestId),
          meetingType: new FormControl(normalized.meetingType),
          meetingPriority: new FormControl(normalized.meetingPriority),
        });
    });

    this.companySelectionForm.setControl('companies', new FormArray(companyControls));
  }

  private setTimeSelectionForm(slots: any[]): void {
    const slotControls = slots.map((slot) => new FormGroup({
      slotDate: new FormControl(slot.slotDate),
      slotId: new FormControl(slot.slotId),
      slotPeriods: this.createSlotPeriodsFormArray(slot.slotPeriods)
    }));
    
    this.timeSelectionForm.setControl('slots', new FormArray(slotControls, this.validateAvailableTime()));
  }

  createSlotPeriodsFormArray(slotPeriods: any[]) {
    return new FormArray(slotPeriods.map(sp => new FormGroup({
      investSlotId: new FormControl(sp.investSlotId),
      description: new FormControl(sp.description),
      endTime: new FormControl(sp.endTime),
      slotPeriodId: new FormControl(sp.slotPeriodId),
      startTime: new FormControl(sp.startTime),
      isAvailable: new FormControl(sp.available === 'Y' || sp.isAvailable)
    })));
  }

  validateAvailableTime(): ValidatorFn {
    const validator: ValidatorFn = (formArrays: FormArray) => {
      const arrays = formArrays.value.map(m => m.slotPeriods);
      const flattenArrays = [].concat.apply([], arrays)
      const result = flattenArrays.map((m: any) => m.isAvailable).filter((f: boolean) => f).length > 0;
      
      return result ? null : { selectAtLeaseOneAvailableTime: true };
    }
    return validator;
  }

  private getDefaultMeetingType(
    attendancePreference?: RegisterInvestorResponse.AttendancePreferenceEnum
  ): InvestorMeetingCreateRequest.MeetingTypeEnum {
    return attendancePreference === RegisterInvestorResponse.AttendancePreferenceEnum.VIRTUAL
      ? InvestorMeetingCreateRequest.MeetingTypeEnum.VIRTUALGROUP
      : InvestorMeetingCreateRequest.MeetingTypeEnum.GROUP;
  }


  private normalizeSelection(
    meetingType?: InvestorMeetingCreateRequest.MeetingTypeEnum,
    meetingPriority?: InvestorMeetingCreateRequest.MeetingPriorityEnum,
  ) {
    const isVirtualPreference = this.attendancePreference === RegisterInvestorResponse.AttendancePreferenceEnum.VIRTUAL;
    const isPhysicalPreference = this.attendancePreference === RegisterInvestorResponse.AttendancePreferenceEnum.PHYSICAL;

    if (isVirtualPreference && meetingType !== InvestorMeetingCreateRequest.MeetingTypeEnum.VIRTUALGROUP) {
      return {
        meetingType: InvestorMeetingCreateRequest.MeetingTypeEnum.VIRTUALGROUP,
        meetingPriority: meetingPriority || this.defaultMeetingPriority,
      };
    }
    if (isPhysicalPreference && meetingType === InvestorMeetingCreateRequest.MeetingTypeEnum.VIRTUALGROUP) {
      return {
        meetingType: this.defaultMeetingType,
        meetingPriority: meetingPriority || this.defaultMeetingPriority,
      };
    }
    return {
      meetingType: meetingType || this.defaultMeetingType,
      meetingPriority: meetingPriority || this.defaultMeetingPriority,
    };
  }

  private setProfileForm(data: RegisterInvestorResponse): void {
    this.profileForm.patchValue({
      coHostName: data.salePersonName,
      brokerName: data.salePersonBrokerName,
      hasSalePerson: data.hasSalePerson !== false,
      seminarSubEvent: data.seminarSubEvent,
      attendancePreference: data.attendancePreference,

      companyName: data.companyName,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      mobileNumber: data.mobileNumber? { number: data.mobileNumber, countryCode: data.countryCode } : null,
      extNumber: data.extNumber,
      jobTitle: data.jobTitle,
      department: data.department,
      countryId: data.countryId,

      institutionalInvestorTypeId: data.institutionalInvestorTypeId,
      otherInstitutionalInvestorType: data.otherInstitutionalInvestorType,

      assistantFirstName: data.assistantFirstName,
      assistantLastName: data.assistantLastName,
      assistantEmail: data.assistantEmail,
      assistantMobileNumber: data.assistantMobileNumber ? { number: data.assistantMobileNumber, countryCode: data.assistantCountryCode } : null,
      assistantExtNumber: data.assistantExtNumber
    });
  }

  saveMeetingRequest(meetingUpdateRequest: InvestorMeetingUpdateRequest) {
    this.registerInvestorService.saveInvestorMeetingRequest(meetingUpdateRequest, () => {
      this.alert.showSuccess(msg.success.dataSaved).then(() => {
        this.captureCompanySelectionSnapshot();
        this.registerInvestorService.fetchInvestorMeetingRequest();
      });
    });
  }

  cancelMeetingRequest(meetingRequestIds: number) {
    this.registerInvestorService.cancelInvestorMeetingRequestById(meetingRequestIds, () => {
      this.registerInvestorService.fetchInvestorMeetingRequest();
    });
  }

  onSaveProfile(profileFormValue: any): void {
    const attendanceChanged = profileFormValue.attendancePreference !== this.attendancePreference;
    const previousAttendance = this.attendancePreference;

    if (attendanceChanged) {
      this.attendancePreference = profileFormValue.attendancePreference;
      this.defaultMeetingType = this.getDefaultMeetingType(this.attendancePreference);
      this.normalizeCompanyMeetingTypes();
    }

    const request: RegisterInvestorUpdateRequest = {
      hasSalePerson: profileFormValue.hasSalePerson !== false,
      salePersonName: profileFormValue.coHostName,
      brokerName: profileFormValue.brokerName ? profileFormValue.brokerName : profileFormValue.coHostName,
      seminarSubEvent: profileFormValue.seminarSubEvent,
      attendancePreference: profileFormValue.attendancePreference,
      companyName: profileFormValue.companyName,
      firstName: profileFormValue.firstName,
      lastName: profileFormValue.lastName,
      email: profileFormValue.email,
      mobileNumber: profileFormValue.mobileNumber ? profileFormValue.mobileNumber.e164Number : null,
      countryCode: profileFormValue.mobileNumber ? profileFormValue.mobileNumber.countryCode : null,
      extNumber: profileFormValue.extNumber,
      jobTitle: profileFormValue.jobTitle,
      department: profileFormValue.department,
      countryId: profileFormValue.countryId,
      institutionalInvestorTypeId: profileFormValue.institutionalInvestorTypeId,
      otherInstitutionalInvestorType: profileFormValue.otherInstitutionalInvestorType,
      assistantFirstName: profileFormValue.assistantFirstName,
      assistantLastName: profileFormValue.assistantLastName,
      assistantEmail: profileFormValue.assistantEmail,
      assistantMobileNumber: profileFormValue.assistantMobileNumber ? profileFormValue.assistantMobileNumber.e164Number : null,
      assistantCountryCode: profileFormValue.assistantMobileNumber ? profileFormValue.assistantMobileNumber.countryCode : null,
      assistantExtNumber: profileFormValue.assistantExtNumber,
    } as RegisterInvestorUpdateRequest;

    this.registerInvestorService.saveRegisterInvestorInfo(request, () => {
      if (attendanceChanged) {
        this.saveCompanySelection(true);
      } else {
        this.alert.showSuccess(msg.success.dataSaved).then(() => {
          this.captureProfileSnapshot();
        });
      }
    }, () => {
      if (attendanceChanged) {
        this.attendancePreference = previousAttendance;
        this.restoreCompanySelectionSnapshot();
      }
    });
  }

  private buildCompanySelectionRequest(): InvestorMeetingUpdateRequest {
    const companiesFormArray = this.companySelectionForm.get('companies') as FormArray;

    const meetingUpdates = companiesFormArray.value.map((company: any) => ({
      investorMeetingRequestId: company.investorMeetingRequestId,
      registerCompanyId: company.registerCompanyId,
      meetingType: company.meetingType,
      priority: company.meetingPriority,
      registerInvestorId: this.investorProfile.registerInvestorId,
    }));

    const seminarSubEventControl = this.profileForm.get('seminarSubEvent');
    return {
      investorMeetingRequests: meetingUpdates,
      seminarSubEvent: seminarSubEventControl ? seminarSubEventControl.value : null,
    };
  }

  saveCompanySelection(reloadFromServer: boolean = false): void {
    const request = this.buildCompanySelectionRequest();

    this.registerInvestorService.saveInvestorMeetingRequest(request, () => {
      if (reloadFromServer) {
        this.investorMeetingRequestService.getMeetingRequestUsingGET1(this.investorProfile.registerInvestorId)
          .subscribe((response: any) => {
            this.setCompanyForm(response.data);
            this.captureCompanySelectionSnapshot();
            this.captureProfileSnapshot();
            this.registerInvestorService.investorMeetingRequest.next(response.data);
            this.alert.showSuccess(msg.success.dataSaved);
          });
      } else {
        this.captureCompanySelectionSnapshot();
        this.registerInvestorService.fetchInvestorMeetingRequest();
        this.alert.showSuccess(msg.success.dataSaved);
      }
    });
  }

  onSaveCompanySelection(companySelection: any): void {
    this.saveCompanySelection(false);
  }

  private normalizeCompanyMeetingTypes(): void {
    const companiesFA = this.companySelectionForm.get('companies') as FormArray;
    companiesFA.controls.forEach(ctrl => {
      const currentType = ctrl.get('meetingType').value;
      const normalized = this.normalizeSelection(currentType);
      ctrl.get('meetingType').setValue(normalized.meetingType);
      ctrl.get('meetingPriority').setValue(this.defaultMeetingPriority);
    });
  }

  onSaveTimeSelection(): void {
    const slotsFormArray = this.timeSelectionForm.get('slots') as FormArray;

    const result: InvestorSlotUpdate[] = [];

    slotsFormArray.value.forEach(slot => {
      slot.slotPeriods.forEach(period => {
        result.push({ 
          investorSlotId: period.investSlotId,
          slotPeriodId: period.slotPeriodId,
          available: period.isAvailable
            ? InvestorSlotUpdate.AvailableEnum.Y
            : InvestorSlotUpdate.AvailableEnum.N
        });
      });
    });

    const request: InvestorSlotUpdateRequest = {
      investorSlots: result
    };

    this.investorSlotService.createAndUpdateInvestorSlotUsingPOST1(request).pipe(
      switchMap(() => this.investorSlotService.getInvestorSlotUsingGET1())
    ).subscribe((response) => {
      const slots = response.data;
      this.setTimeSelectionForm(slots);
      this.captureTimeSelectionSnapshot();
      this.registerInvestorService.investorSlots.next(slots);
      this.alert.showSuccess(msg.success.dataSaved);
    });
  }

}
